import React from 'react';
import { MapPin, Headphones, Shield, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CarRentalHeroV2 = () => {
  const customerAvatars = [
    'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&q=80',
    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&q=80',
    'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&q=80',
    'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&q=80',
  ];

  const navigate = useNavigate()

  return (
    <div className="relative w-full overflow-hidden">
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-20 py-6">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-start ">
          
          {/* Left Side - Custom Grid Layout with Centered Badge */}
          <div className="relative order-2 lg:order-1">
            <div className="relative w-full aspect-square mx-auto">
              {/* 2x2 Grid with rounded corners and gaps */}
              <div className="relative w-full h-[400px] md:h-[650px] grid grid-cols-2">
                
                {/* Top Left Card */}
                <div className="relative rounded-tr-[40px] sm:rounded-tr-[80px] rounded-bl-[40px] sm:rounded-bl-[80px] overflow-hidden shadow-2xl animate-fade-in-scale">
                  <img
                    src="https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=800&q=80"
                    alt="Car showroom"
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Top Right Card */}
                <div className="relative rounded-tl-[40px] sm:rounded-tl-[80px] rounded-br-[40px] sm:rounded-br-[80px] overflow-hidden shadow-2xl animate-fade-in-scale" style={{ animationDelay: '150ms' }}>
                  <img
                    src="https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?w=800&q=80"
                    alt="Luxury car exterior"
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Bottom Left Card */}
                <div className="relative rounded-br-[40px] sm:rounded-br-[80px]  rounded-tl-[40px] sm:rounded-tl-[80px] overflow-hidden shadow-2xl animate-fade-in-scale" style={{ animationDelay: '300ms' }}>
                  <img
                    src="https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?w=600&q=80"
                    alt="Luxury car at night"
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Bottom Right Card */}
                <div className="relative rounded-bl-[80px] sm:rounded-bl-[120px] rounded-tr-[80px] sm:rounded-tr-[120px] overflow-hidden shadow-2xl animate-fade-in-scale" style={{ animationDelay: '450ms' }}>
                  <img
                    src="https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?w=600&q=80"
                    alt="Happy customer"
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Centered Experience Badge - Absolute positioned in the middle */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 animate-scale-bounce" style={{ animationDelay: '600ms' }}>
                  <div className="relative">
                    {/* Main Badge */}
                    <div className="bg-gradient-to-br from-orange-500 to-red-600 text-white rounded-full w-36 h-36 flex flex-col items-center justify-center shadow-2xl border-4 border-white">
                      <div className="text-2xl sm:text-3xl font-bold">10+</div>
                      <div className="text-xs sm:text-sm text-center leading-tight mt-1 px-2">
                        Years of<br />Experience
                      </div>
                    </div>
                    {/* Pulse Ring Animation */}
                    <div className="absolute inset-0 rounded-full bg-orange-400 animate-ping opacity-30"></div>
                    {/* Second Pulse Ring */}
                    <div className="absolute inset-0 rounded-full bg-orange-500 animate-ping-slow opacity-20"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Content Section */}
          <div className="order-1 lg:order-2 space-y-6 animate-fade-in md:mt-10" style={{ animationDelay: '300ms' }}>
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 text-orange-600">
              <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                <MapPin className="w-4 h-4" />
              </div>
              <span className="text-sm font-semibold">Get to know about Us</span>
            </div>

            {/* Main Heading */}
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 leading-tight animate-slide-up">
              Provide high-quality vehicles & service to make your journeys seamless & enjoyable.
            </h1>

            {/* Description */}
            <p className="text-gray-600 text-base md:text-[18px] leading-relaxed text-justify animate-slide-up" style={{ animationDelay: '100ms' }}>
              We offer a diverse range of vehicles, from compact cars ideal for city driving to spacious SUVs for family trips and luxurious models for special events. All our cars are well-maintained, regularly serviced
            </p>

            {/* Features List */}
            <div className="space-y-4 pt-4">
              {/* Feature 1 */}
              <div className="flex items-start space-x-3 " style={{ animationDelay: '200ms' }}>
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center mt-1">
                  <MapPin className="w-4 h-4 text-teal-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg">Convenient Locations</h3>
                  <p className="text-gray-600 text-sm">Multiple pick-up and drop-off locations to suit your travel plans.</p>
                </div>
              </div>

              {/* Feature 2 */}
              <div className="flex items-start space-x-3 " style={{ animationDelay: '300ms' }}>
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center mt-1">
                  <Headphones className="w-4 h-4 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg">Customer-Centric Service</h3>
                  <p className="text-gray-600 text-sm">Our team is always ready to assist you with any inquiries or needs.</p>
                </div>
              </div>

              {/* Feature 3 */}
              <div className="flex items-start space-x-3" style={{ animationDelay: '400ms' }}>
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center mt-1">
                  <Shield className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg">Safety First</h3>
                  <p className="text-gray-600 text-sm">All vehicles undergo thorough cleaning and maintenance to ensure your safety.</p>
                </div>
              </div>
            </div>

            {/* CTA Buttons and Reviews */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-6 md:pt-10 " style={{ animationDelay: '500ms' }}>
              {/* Buttons */}
              <div className="flex flex-wrap gap-3">
                {/* Add Your Car Button */}
                <button onClick={() => navigate("/car")} className="group relative px-6 py-3.5 bg-gray-900 text-white rounded-full font-semibold text-sm shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center space-x-2 overflow-hidden">
                  <span className="relative z-10 flex items-center space-x-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span>Add Your Car</span>
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-gray-800 to-gray-900 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </button>

                {/* Book a Car Button */}
                <button onClick={() => navigate("/car")} className="group relative px-6 py-3.5 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-full font-semibold text-sm shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center space-x-2 overflow-hidden">
                  <span className="relative z-10 flex items-center space-x-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span>Book a Car</span>
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-red-600 to-orange-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </button>
              </div>

              {/* Reviews Section */}
              <div className="flex items-center space-x-3 pl-0 sm:pl-4 sm:border-l-2 border-gray-300">
                {/* Avatar Stack */}
                <div className="flex -space-x-3">
                  {customerAvatars.map((avatar, index) => (
                    <div
                      key={index}
                      className="relative w-10 h-10 rounded-full border-3 border-white shadow-md overflow-hidden hover:scale-110 transition-transform duration-300 animate-pop-in"
                      style={{ 
                        animationDelay: `${700 + index * 100}ms`,
                        zIndex: customerAvatars.length - index 
                      }}
                    >
                      <img
                        src={avatar}
                        alt={`Customer ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>

                {/* Rating */}
                <div className="flex flex-col">
                  <div className="flex items-center space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                    <span className="text-sm font-bold text-gray-900 ml-1">5.0</span>
                  </div>
                  <span className="text-xs text-gray-600">2K+ Reviews</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fade-in-scale {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slide-in-right {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes scale-bounce {
          0% {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.5);
          }
          70% {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1.1);
          }
          100% {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }

        @keyframes pop-in {
          from {
            opacity: 0;
            transform: scale(0);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes ping-slow {
          75%, 100% {
            transform: scale(2.5);
            opacity: 0;
          }
        }

        .animate-fade-in {
          animation: fade-in 0.8s ease-out;
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out forwards;
          opacity: 0;
        }

        .animate-fade-in-scale {
          animation: fade-in-scale 0.7s ease-out forwards;
          opacity: 0;
        }

        .animate-slide-up {
          animation: slide-up 0.6s ease-out forwards;
          opacity: 0;
        }

        .animate-slide-in-right {
          animation: slide-in-right 0.6s ease-out forwards;
          opacity: 0;
        }

        .animate-scale-bounce {
          animation: scale-bounce 0.9s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
          opacity: 0;
        }

        .animate-pop-in {
          animation: pop-in 0.4s ease-out forwards;
          opacity: 0;
        }

        .animate-ping-slow {
          animation: ping-slow 3s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
      `}</style>
    </div>
  );
};

export default CarRentalHeroV2;